import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { PaginatorState } from 'primeng/paginator';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorHistoricoDebito, PrestadorHistoricoDebitoSituacao } from '@/core/api/sicom-api/registro/prestador/prestador-historico.model';
import { PrestadorHistoricoService } from '@/core/api/sicom-api/registro/prestador/prestador-historico.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { DateUtils } from '@/shared/utils/date-utils';
import { TabContainer, TabContent } from '@/shared/components/layout/tab-container/tab-container';

@Component({
    selector: 'app-informacoes-financeiras-prestador',
    standalone: true,
    imports: [CardContent, CustomTableComponent, TabContainer, TabContent],
    templateUrl: './informacoes-financeiras-prestador.html'
})
export class InformacoesFinanceirasPrestador {
    pessoa = input<PessoaDomain>();
    selected = 'ABERTO';
    readonly options = [
        { label: 'Não Pagos', value: 'ABERTO' },
        { label: 'Pagos', value: 'PAGO' },
        { label: 'Prescritos', value: 'PRESCRITO' }
    ];
    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorHistoricoService);
    readonly debitos = signal<PrestadorHistoricoDebito[]>([]);
    readonly first = signal(0);
    readonly rows = signal(10);
    readonly totalRecords = signal(0);
    readonly loading = signal(false);
    readonly debitosTabela = computed(() => this.debitos().map((item) => ({ ...item, dataPagamento: DateUtils.formatDate(item.dataPagamento) })));
    readonly metadata: TableMetadata = {
        headers: {
            idTipoDebito: { type: 'text', value: 'Código', default: '-', hidden: false },
            debito: { type: 'text', value: 'Débito', default: '-', hidden: false },
            numeroDebito: { type: 'text', value: 'Nº Débito', default: '-', hidden: false },
            numeroParcela: { type: 'text', value: 'Parcela', default: '-', hidden: false },
            dataPagamento: { type: 'text', value: 'Pagamento', default: '-', hidden: false }
        },
        title: 'debito',
        defaultSortField: 'idTipoDebito',
        defaultSortOrder: 1
    };

    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            if (pessoa?.id && pessoa.uf) {
                untracked(() => this.carregar('ABERTO'));
            } else {
                this.debitos.set([]);
            }
        });
    }

    alterarAba(situacao: string): void {
        this.first.set(0);
        this.carregar(situacao as PrestadorHistoricoDebitoSituacao);
    }

    onPaginaAlterada(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? 10);
        this.carregar(this.selected as PrestadorHistoricoDebitoSituacao);
    }

    private carregar(situacao: PrestadorHistoricoDebitoSituacao): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) return;

        this.loading.set(true);
        this.service
            .listarDebitos(pessoa.id, pessoa.uf, situacao, Math.floor(this.first() / this.rows()), this.rows())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.loading.set(false))
            )
            .subscribe({
                next: (response) => {
                    this.debitos.set(response.content ?? []);
                    this.totalRecords.set(response.totalElements ?? 0);
                },
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => {
                    this.debitos.set([]);
                    this.totalRecords.set(0);
                }
            });
    }
}
